import React from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock } from "lucide-react";

const msg = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Could not save allocation"; };

export function AllocateProductsDialog({ legacyId, open, onOpenChange }) {
  const [view, setView] = React.useState(null);
  const [grid, setGrid] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    api.get(`/tasks/${legacyId}/unit-allocation`).then((r) => {
      setView(r.data);
      const g = {};
      r.data.products.forEach((p) => {
        const a = { ...p.allocations };
        if (!r.data.allocation_in_use && r.data.shifts.length) {
          const n = r.data.shifts.length;
          r.data.shifts.forEach((s, i) => { a[s.shift_id] = Math.floor(p.qty / n) + (i < p.qty % n ? 1 : 0); });
        }
        g[p.request_id] = a;
      });
      setGrid(g);
    }).catch((e) => toast.error(msg(e)));
  }, [open, legacyId]);
  const set = (rid, sid, v) => setGrid((g) => ({ ...g, [rid]: { ...g[rid], [sid]: Math.max(0, parseInt(v || "0", 10) || 0) } }));
  const total = (rid) => Object.values(grid[rid] || {}).reduce((s, n) => s + n, 0);
  const valid = view && view.products.every((p) => total(p.request_id) === p.qty);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/tasks/${legacyId}/unit-allocation`, {
        allocations: view.products.map((p) => ({ request_id: p.request_id, shifts: grid[p.request_id] })) });
      setView(r.data); toast.success("Products allocated to shifts"); onOpenChange(false);
    } catch (e) { toast.error(msg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="allocate-products-dialog">
        <DialogHeader><DialogTitle>Allocate products to shifts</DialogTitle></DialogHeader>
        {!view ? <p className="text-sm text-slate-500">Loading…</p> : view.shifts.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="allocate-no-shifts">This job has no shifts booked yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="allocate-grid">
              <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                <th className="py-2 pr-3">Product</th>
                {view.shifts.map((s) => (
                  <th key={s.shift_id} className="px-2 py-2 text-center" data-testid={`allocate-col-${s.shift_id}`}>
                    <div className="font-semibold text-slate-700 truncate max-w-[180px] mx-auto" title={s.shift_id}>Shift {s.shift_id}{s.start_time ? ` · ${s.start_time}` : ""}</div>
                    <div className="font-normal">{[s.date, s.vehicle_title].filter(Boolean).join(" · ")}</div>
                  </th>
                ))}
                <th className="py-2 pl-2 text-right">Total</th>
              </tr></thead>
              <tbody>
                {view.products.map((p) => {
                  const t = total(p.request_id);
                  return (
                    <tr key={p.request_id} className="border-b border-slate-100" data-testid={`allocate-row-${p.request_id}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{p.product}</td>
                      {view.shifts.map((s) => (
                        <td key={s.shift_id} className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Input type="number" min={p.locked[s.shift_id] || 0} max={p.qty} value={grid[p.request_id]?.[s.shift_id] ?? 0}
                              onChange={(e) => set(p.request_id, s.shift_id, e.target.value)} className="h-8 w-16 text-center"
                              data-testid={`allocate-input-${p.request_id}-${s.shift_id}`} />
                            {p.locked[s.shift_id] > 0 && <Lock className="h-3.5 w-3.5 text-slate-400" title={`${p.locked[s.shift_id]} already picked`} />}
                          </div>
                        </td>
                      ))}
                      <td className={`py-2 pl-2 text-right tabular-nums font-semibold ${t === p.qty ? "text-emerald-600" : "text-rose-600"}`}
                        data-testid={`allocate-total-${p.request_id}`}>{t}/{p.qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-slate-500">Each unit is tracked individually. Picked units are locked to their shift.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="allocate-cancel">Cancel</Button>
          <Button onClick={save} disabled={!valid || saving || !view?.shifts.length} data-testid="allocate-save">Save allocation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
